import * as jsonpatch from 'fast-json-patch';
import { deleteRequest, getRequest, patchRequest, postRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import AgendaEntryParking from '../../check-in-employees/models/agenda-entry-parking';
import showStatusCodeError from '../../offices/services/logging.service';
import type Parking from '../models/parking';
import { dataCache, PARKINGS_CACHE_KEY } from '../../../core/utils/data-cache';

export const createParking = async (parking: Parking, keycloak: KeycloakContext): Promise<Parking | undefined> => {
  const apiResponse = await postRequest<Parking>('parking', keycloak, parking);

  if (apiResponse.error) {
    return undefined;
  }

  // Invalidate the cached parkings list so the new entry is fetched on next visit.
  dataCache.invalidate(PARKINGS_CACHE_KEY);
  return apiResponse.json;
};

export const updateParking = async (
  originalParking: Parking,
  changedParking: Parking,
  keycloak: KeycloakContext,
): Promise<Parking | undefined> => {
  if (originalParking.id === undefined) {
    return undefined;
  }

  const apiResponse = await patchRequest<Parking>(
    `parking/${originalParking.id}`,
    keycloak,
    jsonpatch.compare(originalParking, changedParking),
  );

  if (apiResponse.error) {
    return undefined;
  }

  // Invalidate the cached parkings list to reflect the updated entry.
  dataCache.invalidate(PARKINGS_CACHE_KEY);
  return apiResponse.json;
};

export const deleteParkingById = async (id: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await deleteRequest(`parking/${id}`, keycloak);

  if (apiResponse.error) {
    return false;
  }

  // Invalidate the cached parkings list so the deleted entry is not shown.
  dataCache.invalidate(PARKINGS_CACHE_KEY);
  return true;
};

export const getParkingById = async (id: string, keycloak: KeycloakContext): Promise<Parking | undefined> => {
  const apiResponse = await getRequest<Parking>(`parking?id=${id}`, keycloak);

  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};

export const getAllParkingsForAuthorEmail = async (keycloak: KeycloakContext): Promise<Parking[] | undefined> => {
  const apiResponse = await getRequest<Parking[]>('parking/parkings-by-author', keycloak);

  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};

export const getAllParkingsForOffice = async (
  keycloak: KeycloakContext,
  officeId: string | undefined,
): Promise<AgendaEntryParking[] | undefined> => {
  if (officeId === undefined) {
    return undefined;
  }

  const apiResponse = await getRequest<AgendaEntryParking[]>(`parking/for-office?OfficeId=${officeId}`, keycloak);

  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }
  return apiResponse.json;
};

export const getAllParkingsForOfficeOnDate = async (
  keycloak: KeycloakContext,
  officeId: string | undefined,
  date: string,
): Promise<AgendaEntryParking[] | undefined> => {
  const apiResponse = await getRequest<AgendaEntryParking[]>(
    `parking/office-id-and-date?officeId=${officeId ?? ''}&date=${date}`,
    keycloak,
  );

  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};
